import { useContext } from "react";
import { Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import type {
  CashAdjustmentData,
  CashBalanceOverrideData,
} from "../lib/explainTypes";
import { RebalanceContext } from "../../RebalanceInfo";
import { useExplainLinksEnabled } from "../ExplainLink";
import CashBalanceOverrideBadge from "../../CashBalanceOverrideBadge";
import { formatCurrency } from "../../../lib/numbers";

interface Props {
  cash: CashAdjustmentData | null;
  accountId: number;
  /**
   * The intraday cash adjustment the run applied
   * (`account.cash_balance_override`). Shown only when the trade proposal has
   * no stored `cashBalanceOverride` for the account.
   */
  cashBalanceOverride?: CashBalanceOverrideData | null;
}

const num = (s: string | null | undefined): number =>
  s == null || s === "" ? 0 : Number(s);

const asPercent = (s: string | null | undefined, decimals = 2): string =>
  s == null ? "—" : `${(num(s) * 100).toFixed(decimals)}%`;

const asPpSigned = (s: string | null | undefined): string => {
  if (s == null) return "—";
  const v = num(s) * 100;
  if (Math.abs(v) < 0.005) return "0 pp";
  return `${v > 0 ? "+" : ""}${v.toFixed(2)} pp`;
};

// Reads `account.cash_adjustment`. The payload is always present
// (the algorithm runs the cash stage for every account); the relevant
// distinctions are:
//   * no cash setting configured: `target.weight === null && target.dollar === null`
//   * cash setting present but didn't fire: `triggered === null`
//   * cash setting fired: `triggered === "min" | "max"`
//     - "max" -> cash exceeded upper band -> Spend Cash (deploy excess)
//     - "min" -> cash fell below lower band -> Raise Cash (reserve more)
// Each case gets its own render path so the common no-fire case
// collapses to one muted line and the fire case gets visual weight.
export default function CashSection({
  cash,
  accountId,
  cashBalanceOverride = null,
}: Props) {
  const { household, rebalance } = useContext(RebalanceContext);
  const accountBalance = household?.accounts?.find(
    (a) => a.id === accountId,
  )?.accountBalance;
  // The stored adjustment of the trade proposal (`cashBalanceOverride` of the
  // rebalance's account) comes first, so this badge shows the same figures as
  // `RebalanceAccountInsights`. The explanation's figures are the fallback for
  // a trade proposal with no stored adjustment.
  const storedOverride =
    rebalance?.accounts?.find((a) => a.id === accountId)?.cashBalanceOverride ??
    null;

  const overrideBadge =
    storedOverride !== null ? (
      <div className="mb-2">
        <CashBalanceOverrideBadge
          adjustment={storedOverride.adjustment}
          amount={storedOverride.amount}
          reportedCashBalance={storedOverride.reportedCashBalance}
          username={storedOverride.username}
          reason={storedOverride.reason}
        />
      </div>
    ) : cashBalanceOverride !== null ? (
      <div className="mb-2">
        <CashBalanceOverrideBadge
          adjustment={num(cashBalanceOverride.delta)}
          amount={num(cashBalanceOverride.cash_balance)}
          reportedCashBalance={num(cashBalanceOverride.reported_cash_balance)}
          reason={cashBalanceOverride.reason}
        />
      </div>
    ) : null;

  return (
    <>
      {overrideBadge}
      <CashAdjustmentBody
        cash={cash}
        accountId={accountId}
        accountBalance={accountBalance}
      />
    </>
  );
}

function CashAdjustmentBody({
  cash,
  accountId,
  accountBalance,
}: {
  cash: CashAdjustmentData | null;
  accountId: number;
  accountBalance: number | null | undefined;
}) {
  const linksEnabled = useExplainLinksEnabled();
  if (!cash) {
    return <div className="text-muted">No cash adjustment.</div>;
  }

  const hasSetting = cash.target.weight != null || cash.target.dollar != null;

  if (!hasSetting) {
    return (
      <div className="text-muted">
        No cash setting configured.
        {linksEnabled && (
          <>
            {" "}
            <Link to={`/clients/accounts/${accountId}/cash-billing`}>
              Configure cash settings
            </Link>
          </>
        )}
      </div>
    );
  }

  if (cash.triggered == null) {
    // Setting present, within band — say so and stop.
    const target =
      cash.target.dollar != null
        ? formatCurrency(num(cash.target.dollar))
        : asPercent(cash.target.weight);
    return (
      <div className="text-muted">
        Within band — no adjustment needed (target {target}, current{" "}
        {asPercent(cash.current_weight)}).
      </div>
    );
  }

  // Triggered: render a focused block. Max band breached = above target =
  // spend cash; min band breached = below target = raise cash.
  const isSpend = cash.triggered === "max";
  const triggerLabel = isSpend ? "Spend Cash" : "Raise Cash";
  const triggerBg = isSpend ? "success" : "warning";

  const targetMode: "dollar" | "weight" =
    cash.target.dollar != null ? "dollar" : "weight";
  const targetDisplay =
    targetMode === "dollar"
      ? formatCurrency(num(cash.target.dollar))
      : asPercent(cash.target.weight);
  const bandDisplay =
    cash.band.min != null || cash.band.max != null
      ? `band ${asPercent(cash.band.min)} – ${asPercent(cash.band.max)}`
      : null;

  const currentDollar =
    accountBalance != null && cash.current_weight != null
      ? num(cash.current_weight) * accountBalance
      : null;

  const scale = num(cash.securities_scale);
  const scaleNote =
    Math.abs(scale - 1) > 0.0001
      ? `securities scaled to ${(scale * 100).toFixed(2)}%`
      : null;

  return (
    <div>
      <div className="mb-2">
        <Badge bg={triggerBg}>{triggerLabel}</Badge>
      </div>
      <dl className="row mb-2">
        <dt className="col-sm-3 text-muted fw-normal">Setting</dt>
        <dd className="col-sm-9">
          {targetDisplay} target
          {bandDisplay && (
            <span className="text-muted ms-2">{bandDisplay}</span>
          )}
        </dd>

        <dt className="col-sm-3 text-muted fw-normal">Current</dt>
        <dd className="col-sm-9">
          {asPercent(cash.current_weight)}
          {currentDollar != null && (
            <span className="text-muted ms-2">
              ({formatCurrency(currentDollar)})
            </span>
          )}
        </dd>

        <dt className="col-sm-3 text-muted fw-normal">Adjustment</dt>
        <dd className="col-sm-9">
          {asPpSigned(cash.adjustment_pp)} toward cash
          {scaleNote && <span className="text-muted ms-2">; {scaleNote}</span>}
        </dd>
      </dl>
      {cash.explanation && (
        <p className="text-muted mb-0 small">{cash.explanation}</p>
      )}
    </div>
  );
}
